import { useMediaQuery } from 'react-responsive';

/**
 * Tailwind CSS 默认断点
 */
export const TAILWIND_BREAKPOINTS = {
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1536,
} as const;

export type BreakpointKey = keyof typeof TAILWIND_BREAKPOINTS;

/**
 * 统一的响应式 Hook
 *
 * ### 语义化布局 (isMobile, isTablet...)
 * - **场景**：根据设备类型渲染**完全不同**的组件结构或逻辑。
 * - **逻辑**：**互斥区间**。例如 `isTablet` 为 true 时，`isMobile` 和 `isDesktop` 均为 false。
 * - **类比**：互斥的布局设计，不能同时存在两个不同的布局。
 */
export function useBreakpoint() {
    const isMobile = useIsMobileLayout();
    const isTablet = useIsTabletLayout();
    const isDesktop = useIsDesktopLayout();

    return {
        isMobile,
        isTablet,
        isDesktop,
    };
}

// ----------------------------------------------------------------------
// 独立 Hooks (单一职责)
// ----------------------------------------------------------------------

/**
 * Mobile: < 768px
 * 适用于：只在手机上显示的逻辑
 */
export function useIsMobileLayout() {
    return useMediaQuery({ maxWidth: TAILWIND_BREAKPOINTS.md - 1 });
}

/**
 * Tablet: 768px <= width < 1024px
 * 适用于：只在平板上显示的逻辑
 */
export function useIsTabletLayout() {
    return useMediaQuery({
        minWidth: TAILWIND_BREAKPOINTS.md,
        maxWidth: TAILWIND_BREAKPOINTS.lg - 1,
    });
}

/**
 * Desktop: >= 1024px
 * 适用于：只在桌面上显示的逻辑
 */
export function useIsDesktopLayout() {
    return useMediaQuery({ minWidth: TAILWIND_BREAKPOINTS.lg });
}
